import { AccordionDetails, styled, Typography } from '@mui/material';
import { AccordionSummary } from '@mui/material';
import { Accordion } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactNode } from 'react';
import React from 'react';

type AppAccordionProps = {
  title: string;
  children: ReactNode;
  defaultExpanded?: boolean;
};

type AppAccordionSummaryProps = {
  children: ReactNode;
  noPadding?: boolean;
};

type AppAccordionContentProps = {
  children: ReactNode;
};

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  border: 'none',
  boxShadow: 'none',
  borderTop: `1px solid ${theme.palette.borderColor?.lightGrey}`,
  margin: '0 !important',
  '&.MuiAccordion-root': {
    margin: 0,
  },
  '&:before': {
    display: 'none',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)<{
  noPadding?: boolean;
}>(({ theme, noPadding }) => ({
  padding: noPadding ? 0 : theme.spacing(4),

  margin: '0 !important',
  '& .MuiAccordionSummary-content': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(4),
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    marginLeft: theme.spacing(4),
    '& .MuiSvgIcon-root': {
      color: theme.palette.text.rootColor,
    },
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(4),
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  fontSize: theme.typography.pxToRem(22),
  lineHeight: 1.5,
  color: '#656565',
  letterSpacing: theme.spacing(0),
}));

// Sub-components
const Summary = ({ children }: AppAccordionSummaryProps) => <>{children}</>;
const Content = ({ children }: AppAccordionContentProps) => <>{children}</>;

// Add display names to help with component identification
Summary.displayName = 'AppAccordion.Summary';
Content.displayName = 'AppAccordion.Content';

const AppAccordion: React.FC<AppAccordionProps> & {
  Summary: React.FC<AppAccordionSummaryProps>;
  Content: React.FC<AppAccordionContentProps>;
} = ({ title, children, defaultExpanded = false }) => {
  let summaryContent: ReactNode = null;
  let mainContent: ReactNode = null;
  let summaryProps = { noPadding: false };

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if ((child.type as any).displayName === 'AppAccordion.Summary') {
        summaryContent = child.props.children;
        summaryProps = {
          noPadding: child.props.noPadding,
        };
      } else if ((child.type as any).displayName === 'AppAccordion.Content') {
        mainContent = child.props.children;
      }
    }
  });

  return (
    <StyledAccordion defaultExpanded={defaultExpanded}>
      <StyledAccordionSummary
        expandIcon={<ExpandMoreIcon />}
        noPadding={summaryProps.noPadding}
      >
        <Title>{title}</Title>
        {summaryContent}
      </StyledAccordionSummary>
      <StyledAccordionDetails>{mainContent}</StyledAccordionDetails>
    </StyledAccordion>
  );
};

// Attach sub-components to the main component
AppAccordion.Summary = Summary;
AppAccordion.Content = Content;

export default AppAccordion;
